import { Flex, notification, Typography, Upload } from 'antd';
import { RcFile } from 'antd/es/upload';
import { UploadFile, UploadProps } from 'antd/lib';
import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';

import { UploadIcon } from '../Icon/Upload';

const { Dragger } = Upload;

/**
 * Enum defining possible error messages for file upload validation
 */
const enum ErrorMessageEnum {
  FILE_SIZE_EXCEEDED = 'FILE_SIZE_EXCEEDED',
  MAX_FILE_REACHED = 'MAX_FILE_REACHED',
  INVALID_FILE = 'INVALID_FILE',
  DUPLICATED_FILE = 'DUPLICATED_FILE',
}

/**
 * Mapping of error types to their corresponding notification content
 */
const ErrorMessageToast: Record<
  ErrorMessageEnum,
  { message: string; description: (msg?: string) => string }
> = {
  [ErrorMessageEnum.FILE_SIZE_EXCEEDED]: {
    message: 'File Size Exceeded',
    description: (msg?: string) =>
      `File size exceeds the maximum limit of ${msg ?? '10'}MB`,
  },
  [ErrorMessageEnum.MAX_FILE_REACHED]: {
    message: 'Max Files Reached',
    description: () => 'The maximum number of files is 10',
  },
  [ErrorMessageEnum.INVALID_FILE]: {
    message: 'Invalid File Type',
    description: (msg?: string) =>
      `Only ${msg?.replace(/\./g, '').toUpperCase() ?? 'MAJ'} file types are supported`,
  },
  [ErrorMessageEnum.DUPLICATED_FILE]: {
    message: 'Duplicate File',
    description: () => 'Upload Failed. Duplicate File',
  },
};

interface IUploadFile {
  // Accepted file extensions (e.g., '.jpg,.png')
  accept: string;
  // Maximum file size in MB
  limitMB: number;
  // Current list of uploaded files
  filesList: RcFile[];
  // State setter for filesList
  setFilesList: Dispatch<SetStateAction<RcFile[]>>;
  // Maximum number of files allowed to be uploaded
  remainFiles: number;
  // Custom function to validate against duplicate files
  validateDuplicated?: (file: RcFile) => boolean;
  // Callback when a file is successfully validated and added
  onSubmitFiles?: (file: RcFile) => void;
  // Custom file removal handler
  removeFile?: (file: UploadFile) => void | boolean | Promise<void | boolean>;
  // Counter for successfully uploaded files
  counterSuccess: number;
  // Render prop for displaying uploaded files
  children: (file: RcFile) => React.ReactNode;
  // Classname container that contains file list
  classNameRenderFileList?: string;
  // Flag to show label count upload successfully
  isShowLabelUploadSuccess?: boolean;
}

/**
 * A customizable file upload component with drag-and-drop support,
 * file validation, and error notifications.
 *
 * Features:
 * - Drag and drop or click to upload
 * - File type validation
 * - File size limitation
 * - Maximum files count limitation
 * - Duplicate file detection
 * - Customizable file display via children render prop
 * - Error notifications for validation failures
 */
export const UploadFileComponent: FC<IUploadFile> = ({
  accept,
  limitMB,
  filesList,
  setFilesList,
  remainFiles,
  validateDuplicated,
  onSubmitFiles,
  removeFile,
  counterSuccess,
  children,
  classNameRenderFileList = 'max-h-96',
  isShowLabelUploadSuccess = true,
}) => {
  const [api, contextHolder] = notification.useNotification();

  const LIMIT_SIZE = useMemo(() => limitMB * 1024 * 1024, [limitMB]);

  /**
   * Displays an error notification for a failed upload attempt
   * @param file - The file that failed validation
   * @param errorType - Type of validation error
   */
  const openNotification = useCallback(
    (file: RcFile, errorType: ErrorMessageEnum, msg?: string) => {
      api.error({
        key: file.uid,
        ...ErrorMessageToast[errorType],
        description: ErrorMessageToast[errorType].description(msg),
      });
    },
    [api]
  );

  /**
   * Extracts file extension from filename
   * @param fileName - The name of the file
   * @returns The file extension in lowercase with leading dot (e.g., '.jpg')
   */
  const getFileExtension = (fileName: string) => {
    const ext = fileName.split('.').pop();
    return ext ? `.${ext.toLocaleLowerCase()}` : '';
  };

  // Process accepted file types into an array of extensions
  const allowedTypes: string[] = accept
    .split(',')
    .map((ext: string) => (ext = ext.trim().toLowerCase()))
    .filter(Boolean);

  /**
   * Configuration for the Ant Design Dragger component
   * Handles all file validation before upload
   */
  const draggerProps: UploadProps = useMemo(
    () => ({
      name: 'files',
      multiple: true,
      accept,
      method: 'POST',
      fileList: [],
      beforeUpload: (file: RcFile) => {
        // Validate file type
        if (!allowedTypes.includes(getFileExtension(file.name))) {
          openNotification(file, ErrorMessageEnum.INVALID_FILE, accept);
          return false;
        }
        // Validate file size
        if (file.size > LIMIT_SIZE) {
          openNotification(
            file,
            ErrorMessageEnum.FILE_SIZE_EXCEEDED,
            limitMB.toString()
          );
          return false;
        }
        // Validate maximum files count
        if (filesList.length >= remainFiles) {
          openNotification(file, ErrorMessageEnum.MAX_FILE_REACHED);
          return false;
        }
        // Validate for duplicates
        if (validateDuplicated?.(file)) {
          openNotification(file, ErrorMessageEnum.DUPLICATED_FILE);
          return false;
        }

        filesList.push(file);
        setFilesList([...filesList]);
        onSubmitFiles?.(file);

        return false;
      },
      onRemove: removeFile,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      accept,
      removeFile,
      LIMIT_SIZE,
      filesList,
      remainFiles,
      validateDuplicated,
      setFilesList,
      onSubmitFiles,
      openNotification,
    ]
  );

  return (
    <>
      {contextHolder}
      <Flex hidden={filesList.length === remainFiles || remainFiles <= 0}>
        <Dragger className="w-full" {...draggerProps}>
          <Flex justify="center" align="center">
            <p className="ant-upload-drag-icon">
              <UploadIcon />
            </p>
          </Flex>
          <p className="ant-upload-text">
            <span className="font-semibold" style={{ color: '#EA580C' }}>
              Click to upload
            </span>{' '}
            or drag and drop
          </p>
          <p className="ant-upload-hint">
            Supported file type: {accept}, with a maximum upload file size of{' '}
            {limitMB}MB
          </p>
        </Dragger>
      </Flex>
      <Flex
        vertical
        className={`my-3 ${classNameRenderFileList} overflow-auto`}
      >
        <Typography.Text
          hidden={!isShowLabelUploadSuccess || remainFiles === 1}
          className="font-semibold text-gray-700"
        >
          {counterSuccess}/{remainFiles} Files Uploaded Successfully
        </Typography.Text>
        {filesList.map(children)}
      </Flex>
    </>
  );
};
